import { type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import {
  addMinutes,
  Breakpoints,
  Button,
  ButtonColor,
  ButtonWidth,
  DateFormat,
  formatDate,
  formatName,
  formatTime,
  IconCheckmark,
  isDateAfter,
  MINUTE,
  pluralize,
  ProfileImage,
  ProfileImageStack,
  Rating,
  RatingSize,
  Tag,
  TagColor,
  ToastDuration,
  ToastType,
  useMediaQuery,
  useToast,
} from '@leland-dev/leland-ui-library';
import { type GraphQLFormattedError } from 'graphql';
import { uniqBy } from 'lodash-es';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, {
  type FC,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import mxpnl from '../../utils/analytics/mixpanel';
import { getFirstErrorMessage } from '../../utils/apollo';
import { categoryUrnToSlug } from '../../utils/category';
import { setLocalCheckoutOrderInputs } from '../../utils/checkout';
import {
  getCoachUrl,
  getCTATooltipTextForBlockedUser,
} from '../../utils/coach';
import { COACH_SITE_URL } from '../../utils/constants';
import { formatTimeRange } from '../../utils/datetime';
import { wasMutationSuccessful } from '../../utils/mutations';
import { DEFAULT_REVIEW_FORM_VALUES } from '../../utils/review';
import { generateSearchPath } from '../../utils/search';
import { getUrlObject } from '../../utils/url';
import { useLoginModal } from '../LoginModal';
import { Oops } from '../Oops';
import Breadcrumbs, {
  type BreadcrumbsProps,
  goalNameToBreadcrumbsLabel,
} from '../breadcrumbs/Breadcrumbs';
import CoachCategorySpecificInfo from '../coach/CoachCategorySpecificInfo';
import ReviewSlider from '../coach/ReviewSlider';
import { useAllCoachReviewsModal } from '../coach/modals/AllCoachReviewsModal';
import { useSalesModal } from '../common/SalesModal';
import { useGuestSignupModal } from '../event/GuestSignupModal';
import Page from '../page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../page/PageContent';
import { useLeaveAReviewModal } from '../review/modals/LeaveAReviewModal';
import { useOutcomeModal } from '../review/modals/OutcomeModal';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import CountDownButton from './CountDownButton';
import { GroupClassImage } from './GroupClassImage';
import { useMultiEventRegistrationModal } from './MultiEventRegistrationModal';
import {
  type ClassPageCoachFragment,
  ClassPageGetHydratedClassBySlugDocument,
  ClassPageGetStaticClassBySlugDocument,
  type ClassPageGetStaticClassBySlugQuery,
  type ClassPageGetStaticClassBySlugQueryVariables,
  type ClassPageHydratedClassFragment,
  type ClassPageSessionFragment,
  type ClassPageStaticClassFragment,
  useClassPageCreateOrderMutation,
  useClassPageEnrollFreeEventMutation,
  useClassPageGetHydratedClassBySlugQuery,
  useClassPageUnenrollFreeEventMutation,
} from './__generated-gql-types__/ClassPage.generated';
export interface ClassPageStaticProps {
  staticClassData: ClassPageStaticClassFragment;
  pageContentProps: PageContentDataProps;
}

export const getClassPageStaticData: (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  classSlug: string,
) => Promise<{
  props: Possible<ClassPageStaticProps>;
  errors?: Possible<readonly GraphQLFormattedError[]>;
}> = async (apolloClient, classSlug) => {
  const { data, errors } = await apolloClient.query<
    Possible<ClassPageGetStaticClassBySlugQuery>,
    ClassPageGetStaticClassBySlugQueryVariables
  >({
    query: ClassPageGetStaticClassBySlugDocument,
    variables: { classSlug },
  });

  const pageContentProps = await getStaticPageContentQuery();

  return {
    props: data?.groupClassBySlug
      ? {
          staticClassData: data?.groupClassBySlug,
          pageContentProps,
        }
      : null,
    errors,
  };
};

export const useClassPageRehydratedData = (classSlug: string) => {
  return useClassPageGetHydratedClassBySlugQuery({ variables: { classSlug } });
};

export const ClassPageRefetchQueries = [
  ClassPageGetHydratedClassBySlugDocument,
];

interface Props {
  error: boolean;
  classData: ClassPageHydratedClassFragment;
  pageContentProps: PageContentDataProps;
  reload: () => void;
  trackingPrefix: string;
}

const ClassPage: FC<Props> = ({
  error,
  classData,
  pageContentProps,
  reload,
  trackingPrefix,
}) => {
  const { showToast } = useToast();
  const { currentUser } = useAuth();
  const router = useRouter();
  const { isLexEnabled } = useLex();
  const [hasReviewed, setHasReviewed] = useState(false);

  const isSm = useMediaQuery(Breakpoints.SM);
  const isMd = useMediaQuery(Breakpoints.MD);

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setIsAllReviewsModalOpen, Modal: AllCoachReviewsModal } =
    useAllCoachReviewsModal();
  const { setOpen: setIsReviewModalOpen, Modal: LeaveAReviewModal } =
    useLeaveAReviewModal();
  const { setOpen: setIsOutcomeModalOpen, Modal: OutcomeModal } =
    useOutcomeModal();
  const {
    setOpen: setMultiEventRegistrationModalOpen,
    Modal: MultiEventRegistrationModal,
  } = useMultiEventRegistrationModal();
  const { setOpen: setGuestSignupModalOpen, Modal: GuestSignupModal } =
    useGuestSignupModal();

  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();

  const { setOpen: setSalesModalOpen, Modal: SalesModal } = useSalesModal();

  const [unenrollFreeEventAction] = useClassPageUnenrollFreeEventMutation();
  const [enrollFreeEventAction] = useClassPageEnrollFreeEventMutation();

  const onReviewSuccess = () => {
    setHasReviewed(true);
    setIsReviewModalOpen(false);
    setIsOutcomeModalOpen(true);
  };

  const [sessionForEnroll, setSessionForEnroll] =
    useState<Nullable<ClassPageSessionFragment>>(null);

  const { upcomingSession, otherSessions, noOpenSessions } = useMemo(() => {
    if (classData.upcomingSessionsV2 ?? classData.userEnrolledInSessionsV2) {
      const allSessions = uniqBy(
        [
          ...(classData.upcomingSessionsV2 ?? []),
          ...(classData.userEnrolledInSessionsV2 ?? []),
        ],
        'id',
      )
        .filter(
          (s) =>
            s.userIsEnrolled ||
            s.totalAttendees < (classData.maximumAttendees ?? 0),
        )
        .sort((a, b) => {
          if (a.userIsEnrolled === b.userIsEnrolled) {
            return (a.startAt ?? 0) - (b.startAt ?? 0);
          }
          return a.userIsEnrolled ? -1 : 1;
        });

      return {
        upcomingSession: allSessions[0],
        otherSessions: allSessions.slice(1),
        noOpenSessions: allSessions.length === 0,
      };
    }

    return {};
  }, [
    classData.upcomingSessionsV2,
    classData.userEnrolledInSessionsV2,
    classData.maximumAttendees,
  ]);

  const coach = classData.coach;
  const titleFragments = [
    coach ? `${classData.name} by ${formatName(coach.user)}` : classData.name,
  ];
  if (classData.category) {
    titleFragments.push(
      `${classData.category.name} ${classData.isFreeEvent ? 'Free Event' : 'Group Class'}`,
    );
  }
  const title = titleFragments.join(' | ');

  const [createOrder, { loading: createOrderLoading }] =
    useClassPageCreateOrderMutation();

  const currentClassCategory = useMemo(
    () =>
      coach?.categoryInformationList?.find(
        (catInfo) => catInfo.category.id === classData.category?.id,
      ),
    [classData.category?.id, coach?.categoryInformationList],
  );

  const isUserBlocked = !!classData.coach?.coachingRelationship?.blockedByCoach;
  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    classData.coach?.user.firstName,
  );

  const handleEnroll = async (session?: Possible<ClassPageSessionFragment>) => {
    const sessionId = session?.id;
    if (coach?.id && sessionId) {
      if (currentUser) {
        if (!currentUser.applicant) {
          // if currentUser only has a coach account, redirect them to signup as applicant
          return router.push(
            getUrlObject('/signup', {
              redirect_url: router.asPath,
            }),
          );
        }

        if (classData.isFreeEvent) {
          const res = await enrollFreeEventAction({
            variables: {
              id: sessionId,
            },
            optimisticResponse: {
              enrollFreeEvent: {
                id: sessionId,
                userIsEnrolled: true,
              },
            },
          });

          if (wasMutationSuccessful(res)) {
            showToast({
              type: ToastType.SUCCESS,
              duration: ToastDuration.NORMAL,
              message: 'You have successfully enrolled.',
            });
            mxpnl.track('Enroll Free Event', {
              eventId: classData.id,
              eventName: classData.name,
              sessionId,
              location: `${trackingPrefix} - ClassPage`,
            });

            if (isDateAfter(currentUser.createdAt + MINUTE, Date.now())) {
              void setSalesModalOpen(true);
            } else {
              if (
                !(await openPhoneNumberModalIfNecessary()) &&
                isLexEnabled(LexSlug.FEATURE_MULTI_EVENT_REGISTRATION)
              ) {
                setMultiEventRegistrationModalOpen(true);
              }
            }
          } else {
            showToast({
              type: ToastType.ERROR,
              duration: ToastDuration.NORMAL,
              message: 'Error enrolling in event. Please try again.',
            });
          }
        } else {
          const res = await createOrder({
            variables: {
              coachUrn: coach.id,
              classOrder: {
                classSessionUrn: sessionId,
              },
              numberOfCharges: 1,
            },
          });

          if (!wasMutationSuccessful(res)) {
            showToast({
              type: ToastType.ERROR,
              duration: ToastDuration.NORMAL,
              message:
                'Error processing enrollment in session. Please try again.',
            });

            console.warn(getFirstErrorMessage(res.errors));
          } else {
            await router.push(`/checkout?order=${res.data?.createOrder.id}`);
          }
        }
      } else {
        if (classData.isFreeEvent) {
          setSessionForEnroll(session);
          if (isLexEnabled(LexSlug.FEATURE_GUEST_EVENT_SIGNUP)) {
            return setGuestSignupModalOpen(true);
          } else {
            return setLoginModalOpen(true);
          }
        }
        setLocalCheckoutOrderInputs({
          coachUrn: coach.id,
          categoryId: classData.category?.id,
          hourlyOrder: undefined,
          packageOrder: undefined,
          classOrder: {
            classSessionUrn: sessionId,
          },
        });

        await router.push('/checkout');
      }
    }
  };

  const onUnenrollFreeEvent = useCallback(async () => {
    if (!upcomingSession?.id) {
      return;
    }
    const res = await unenrollFreeEventAction({
      variables: { id: upcomingSession.id },
      optimisticResponse: {
        unenrollFreeEvent: {
          id: upcomingSession.id,
          userIsEnrolled: false,
        },
      },
    });

    if (wasMutationSuccessful(res)) {
      showToast({
        type: ToastType.SUCCESS,
        duration: ToastDuration.NORMAL,
        message: 'You have successfully unenrolled.',
      });
      mxpnl.track('Unenroll Free Event', {
        eventId: classData.id,
        eventName: classData.name,
        sessionId: upcomingSession,
        location: `${trackingPrefix} - ClassPage`,
      });
    } else {
      showToast({
        type: ToastType.ERROR,
        duration: ToastDuration.NORMAL,
        message: 'Error unenrolling from session. Please try again.',
      });
    }
  }, [
    classData.id,
    classData.name,
    showToast,
    trackingPrefix,
    unenrollFreeEventAction,
    upcomingSession,
  ]);

  const breadcrumbs: BreadcrumbsProps['breadcrumbs'] = useMemo(() => {
    if (classData.category) {
      const goal = classData.category.goal;
      return [
        {
          label: goalNameToBreadcrumbsLabel(goal, isSm),
          url: generateSearchPath({
            goal: classData.category.goal,
            categorySlug: null,
            subCategorySlug: null,
          }),
        },
        {
          label: classData.category.name,
          url: generateSearchPath({
            goal,
            categorySlug: classData.category.slug,
            subCategorySlug: null,
          }),
        },
      ];
    }
    return [];
  }, [classData.category, isSm]);

  const attendeeFacePiles = upcomingSession?.topFacePiles;
  const totalAttendees = upcomingSession?.totalAttendees;

  return (
    <Page
      title={title}
      imageUrl={classData.imageLink ?? undefined}
      noIndex={classData.isFreeEvent ?? undefined}
    >
      <PageContent initialData={pageContentProps}>
        {error ? (
          <Oops
            className="px-4"
            description="We encountered an error fetching class details. Please try again."
            retry={reload}
          />
        ) : classData ? (
          <>
            {isMd ? null : (
              <GroupClassImage
                containerClassName="w-full"
                className="object-cover"
                name={classData.name}
                imageLink={classData.imageLink}
                sizes={{ dflt: '100%' }}
              />
            )}
            <div className="mx-auto my-4 flex w-full max-w-6xl flex-col items-start justify-start md:px-4 lg:my-10 lg:px-6">
              {isMd ? null : (
                <div className="px-4 md:px-0">
                  <Breadcrumbs breadcrumbs={breadcrumbs} />
                  <div className="my-6 flex w-full flex-col items-start">
                    <h1 className="text-left text-4xl font-medium text-leland-gray-dark">
                      {classData.name}
                    </h1>
                  </div>
                  {coach && currentClassCategory ? (
                    <CoachProfileLink
                      coach={coach}
                      profilePictureClassName="h-10 w-10"
                      categoryId={currentClassCategory.category.id ?? ''}
                    />
                  ) : null}
                </div>
              )}
              <div className="flex w-full grid-cols-12 flex-col-reverse gap-4 md:gap-14 lg:grid">
                <div className="col-span-8 flex flex-col items-start justify-start whitespace-pre-line">
                  {isMd ? (
                    <>
                      <Breadcrumbs breadcrumbs={breadcrumbs} />
                      <div className="px-4 md:px-0">
                        <div className="my-6 flex w-full flex-col items-start">
                          <h1 className="text-left text-4xl font-medium text-leland-gray-dark">
                            {classData.name}
                          </h1>
                        </div>
                        {coach && currentClassCategory ? (
                          <CoachProfileLink
                            coach={coach}
                            profilePictureClassName="h-10 w-10"
                            categoryId={currentClassCategory.category.id ?? ''}
                          />
                        ) : null}
                      </div>
                    </>
                  ) : null}
                  {classData.description ? (
                    <>
                      <p className="mt-8 px-4 text-lg text-leland-gray-light md:px-0">
                        {classData.description}
                      </p>
                      <hr className="my-10 w-full sm:my-12" />
                    </>
                  ) : null}
                  {classData.keyTakeaways?.length ? (
                    <div className="px-4 md:px-0">
                      <ClassInfo heading="What you'll get out of this event">
                        {classData.keyTakeaways.map((keyTakeaway, index) => (
                          <div
                            key={index}
                            className="mt-2 flex items-start justify-start"
                          >
                            <IconCheckmark className="mt-0.5 size-5 shrink-0 text-leland-primary" />
                            <p className="ml-2 font-normal text-leland-gray-light">
                              {keyTakeaway}
                            </p>
                          </div>
                        ))}
                      </ClassInfo>
                    </div>
                  ) : null}

                  {classData.targetAudience ? (
                    <>
                      <hr className="my-10 w-full sm:my-12" />
                      <p className="px-4 text-2xl font-medium text-leland-gray-dark md:px-0">
                        This event is great for{' '}
                        <span className="text-leland-gray-extra-light">
                          {classData.targetAudience}
                        </span>
                      </p>
                    </>
                  ) : null}

                  {classData.coachingFocusList &&
                  classData.coachingFocusList.length > 0 ? (
                    <div className="mt-7 px-4 text-lg text-leland-gray-light md:px-0">
                      This event covers:{' '}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {classData.coachingFocusList.map((offering) => (
                          <Tag
                            key={offering.id}
                            text={offering.name}
                            tagColor={TagColor.TRANSPARENT}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <hr className="my-10 w-full sm:my-12" />

                  {coach && currentClassCategory ? (
                    <div className="mb-6 w-full px-4 md:px-0">
                      <CoachProfileLink
                        coach={coach}
                        headline={currentClassCategory.headline ?? ''}
                        profilePictureClassName="size-14 md:size-10"
                        categoryId={currentClassCategory.category.id ?? ''}
                        showProfileLink
                      />
                      <CoachCategorySpecificInfo
                        coach={coach}
                        categoryInfo={currentClassCategory}
                      />
                      <ReviewSlider
                        className="mt-6"
                        coach={coach}
                        viewAllReviews={() => setIsAllReviewsModalOpen(true)}
                        categoryUrn={currentClassCategory.category.id}
                      />
                    </div>
                  ) : null}
                </div>

                {/* SESSIONS SIDEBAR */}
                <div className="col-span-4 flex h-fit flex-col items-start justify-start rounded-lg sm:sticky sm:top-40 md:border">
                  {isMd ? (
                    <GroupClassImage
                      containerClassName="w-full"
                      className="rounded-t-md object-cover"
                      name={classData.name}
                      imageLink={classData.imageLink}
                      sizes={{ dflt: '100%' }}
                    />
                  ) : (
                    <hr className="mt-6 w-full" />
                  )}

                  <div className="ml-5 mt-6 flex w-full items-center justify-start px-4 text-leland-gray-light md:px-0">
                    {upcomingSession ? (
                      <div className="flex flex-col items-start">
                        <p className="text-left text-xl font-medium text-leland-gray-dark">
                          {formatDate(
                            upcomingSession.startAt,
                            DateFormat.LONG_DAY_OF_WEEK,
                          )}
                          {', '}
                          {formatDate(
                            upcomingSession.startAt,
                            DateFormat.SHORT_MONTH_DAY,
                          )}
                        </p>
                        <p className="text-left">
                          from{' '}
                          {formatTimeRange(
                            upcomingSession.startAt,
                            addMinutes(
                              upcomingSession.startAt,
                              classData.durationInMinute ?? 0,
                            ),
                          )}
                        </p>
                        {attendeeFacePiles ? (
                          <div className="mt-4 flex">
                            <ProfileImageStack
                              profiles={attendeeFacePiles.map(
                                (pictureLink) => ({
                                  pictureLink: pictureLink,
                                  firstName: null,
                                  lastName: null,
                                }),
                              )}
                            />
                            <p className="ml-3 text-lg text-leland-gray-extra-light">
                              {totalAttendees}{' '}
                              {totalAttendees == 1 ? 'person' : 'people'} going
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <hr className="mt-6 w-full" />

                  {classData?.userEnrolledInSessionsV2?.[0]?.totalAttendees &&
                  classData.userEnrolledInSessionsV2[0].totalAttendees > 2 ? (
                    <>
                      <div className="flex w-full items-center justify-start p-4 text-base">
                        <p className="text-base font-normal text-leland-gray-light">
                          {classData.userEnrolledInSessionsV2[0].totalAttendees}{' '}
                          people registered
                        </p>
                        <div className="flex grow justify-end space-x-4">
                          <ProfileImageStack
                            profiles={classData.userEnrolledInSessionsV2[0].topFacePiles.map(
                              (pictureLink) => ({
                                pictureLink: pictureLink,
                                firstName: null,
                                lastName: null,
                              }),
                            )}
                          />
                          <p className="text-base font-medium text-leland-gray-dark">
                            {
                              classData.userEnrolledInSessionsV2[0]
                                .totalAttendees
                            }
                          </p>
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div className="flex w-full flex-col gap-2 p-4 md:p-4">
                    {upcomingSession?.userIsEnrolled &&
                    upcomingSession.startAt ? (
                      <>
                        <CountDownButton
                          date={upcomingSession.startAt}
                          sessionId={upcomingSession.id}
                          blockedStateTooltip={
                            isUserBlocked ? blockedUserCTAText : null
                          }
                          isFreeEvent={classData.isFreeEvent}
                        />
                        {classData.isFreeEvent ? (
                          <div>
                            <Button
                              label="Cancel registration"
                              width={ButtonWidth.FULL}
                              onClick={onUnenrollFreeEvent}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : noOpenSessions ? (
                      <div>
                        <Button
                          label={
                            classData.isFreeEvent
                              ? 'This event has passed'
                              : 'There are no upcoming sessions'
                          }
                          buttonColor={ButtonColor.GRAY}
                          width={ButtonWidth.FULL}
                          disabled
                        />
                      </div>
                    ) : (
                      <Button
                        label={
                          classData.isFreeEvent
                            ? 'Register for free'
                            : 'Register'
                        }
                        buttonColor={ButtonColor.BLACK}
                        disabled={createOrderLoading || isUserBlocked}
                        tooltipContent={
                          isUserBlocked ? blockedUserCTAText : null
                        }
                        width={ButtonWidth.FULL}
                        onClick={() => handleEnroll(upcomingSession)}
                      />
                    )}
                  </div>

                  {isMd ? null : <hr className="mt-6 w-full" />}

                  {otherSessions == null ||
                  otherSessions.length === 0 ? null : (
                    <div className="mt-6 flex w-full flex-col gap-2">
                      <p className="text-leland-gray-light">More sessions</p>

                      {otherSessions?.map((otherSession) => (
                        <div
                          key={otherSession.id}
                          className="rounded-md bg-leland-gray-hover p-4"
                        >
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium">
                                {formatDate(otherSession.startAt)}
                              </p>
                              <p className="text-leland-gray-light">
                                {formatTimeRange(
                                  otherSession.startAt,
                                  addMinutes(
                                    otherSession.startAt,
                                    classData.durationInMinute ?? 0,
                                  ),
                                )}
                              </p>
                            </div>

                            <div>
                              {otherSession.userIsEnrolled ||
                              (otherSession.totalAttendees ?? 0) >=
                                (classData.maximumAttendees ?? 0) ? (
                                <Button
                                  label={
                                    otherSession.userIsEnrolled
                                      ? 'Enrolled'
                                      : 'Session Full'
                                  }
                                  disabled
                                />
                              ) : (
                                <Button
                                  label="Enroll"
                                  disabled={isUserBlocked}
                                  tooltipContent={
                                    isUserBlocked ? blockedUserCTAText : null
                                  }
                                  onClick={() => handleEnroll(otherSession)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {coach ? (
              <AllCoachReviewsModal
                coach={coach}
                hasReviewed={hasReviewed}
                setReviewModalOpen={() => {
                  setIsAllReviewsModalOpen(false);
                  setIsReviewModalOpen(true);
                }}
                categoryUrn={currentClassCategory?.category.id}
              />
            ) : null}
            {coach && classData?.category?.id ? (
              <LeaveAReviewModal
                coach={coach}
                defaultValues={{
                  ...DEFAULT_REVIEW_FORM_VALUES,
                  categoryReviews: [{ categoryUrn: classData.category.id }],
                }}
                onSuccess={onReviewSuccess}
              />
            ) : null}
            {currentUser?.applicant?.id &&
            coach?.categoryInformationList &&
            classData?.category?.id ? (
              <OutcomeModal
                categoryInfo={coach.categoryInformationList}
                coachName={coach.user.firstName}
                defaultValues={{
                  applicantUrn: currentUser.applicant.id,
                  categoryUrn: classData.category.id,
                }}
              />
            ) : null}
          </>
        ) : null}
      </PageContent>

      <PhoneNumberModal
        onClose={() => {
          if (isLexEnabled(LexSlug.FEATURE_MULTI_EVENT_REGISTRATION)) {
            setMultiEventRegistrationModalOpen(true);
          }
        }}
      />
      {sessionForEnroll ? (
        <SalesModal
          sectionInfo={`You are enrolled in "${classData.name}" on ${formatDate(
            sessionForEnroll.startAt,
            DateFormat.LONG_STANDARD,
          )} at ${formatTime(sessionForEnroll.startAt)}.`}
          sectionTitle="You have successfully enrolled."
        />
      ) : null}

      <LoginModal
        onApplicantLogin={() => {
          setLoginModalOpen(false);
          return handleEnroll(sessionForEnroll);
        }}
        onCoachLogin={() => {
          window.location.href = COACH_SITE_URL;
        }}
      />
      <MultiEventRegistrationModal
        event={classData}
        trackingPrefix={trackingPrefix}
      />
      <GuestSignupModal
        onSignup={() => {
          setGuestSignupModalOpen(false);
          return handleEnroll(sessionForEnroll);
        }}
        onLoginClick={() => {
          setGuestSignupModalOpen(false);
          setLoginModalOpen(true);
        }}
      />
    </Page>
  );
};
interface CoachProfileLinkProps {
  coach: ClassPageCoachFragment;
  headline?: string;
  profilePictureClassName: string;
  categoryId: string;
  showProfileLink?: boolean;
}

const CoachProfileLink: FC<CoachProfileLinkProps> = ({
  coach,
  headline,
  profilePictureClassName,
  categoryId,
  showProfileLink = false,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const coachProfileLink = getCoachUrl({
    coachSlug: coach.slug,
    categorySlug: categoryUrnToSlug(categoryId ?? ''),
  });

  return (
    <div className="flex w-full items-center justify-between">
      <div
        className={`flex w-full items-start gap-2 md:flex-row md:items-center ${
          showProfileLink ? 'flex-col' : 'items-center'
        }`}
      >
        <div
          className={`flex items-center justify-between md:w-auto ${
            showProfileLink ? 'w-full' : ''
          }`}
        >
          <ProfileImage
            className={`shrink-0 ${profilePictureClassName}`}
            {...coach.user}
          />
          {isMd ? null : showProfileLink ? (
            <Link
              href={coachProfileLink}
              className="-ml-0.5 self-start text-sm font-medium text-leland-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button label="View Profile" />
            </Link>
          ) : null}
        </div>

        <div className="flex flex-col">
          <div className={`flex ${showProfileLink ? null : 'flex-col'}`}>
            <h2 className="text-lg font-medium text-leland-gray-dark">
              Hosted By{' '}
              <Link
                href={coachProfileLink}
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatName({
                  firstName: coach.user.firstName,
                  lastName: coach.user.lastName,
                })}
              </Link>
            </h2>
            {coach.reviewTotal > 0 ? (
              <div className="flex items-center gap-1.5">
                <Rating
                  totalStars={1}
                  rate={coach.averageReviewRate}
                  showRate
                  size={RatingSize.MEDIUM}
                />
                <p className="whitespace-nowrap text-lg text-leland-gray-extra-light">
                  {pluralize(coach.reviewTotal, 'Review')}
                </p>
              </div>
            ) : null}
          </div>

          <p className="text-leland-gray-light">{headline}</p>
        </div>
      </div>
      {showProfileLink && isMd ? (
        <Link
          href={coachProfileLink}
          className="-ml-0.5 self-start text-sm font-medium text-leland-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button label="View Profile" />
        </Link>
      ) : null}
    </div>
  );
};

const ClassInfo: FC<PropsWithChildren<{ heading: string; info?: string }>> = ({
  heading,
  info,
  children,
}) => {
  return (
    <div className="flex w-full flex-col items-start justify-center text-left text-lg font-normal leading-snug">
      <h2 className="text-2xl font-medium text-leland-gray-dark">{heading}</h2>
      {info ? (
        <p className="mt-2 whitespace-pre-line text-lg font-normal text-leland-gray-light">
          {info}
        </p>
      ) : null}
      {children}
    </div>
  );
};

export default ClassPage;
